import { Location } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, RouteReuseStrategy, withComponentInputBinding, withPreloading, withRouterConfig } from '@angular/router';
import { TranslocoHttpLoader } from '@hunting-nature/shared-core/services/transloco-loader';
import { computeDefaultLanguage } from '@hunting-nature/shared-core/utils/compute-default-language';
import { TabType } from '@hunting-nature/shared-core/enums/tab-bar.enum';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { AbstractSecurityStorage, authInterceptor, provideAuth } from 'angular-auth-oidc-client';
import gsap from 'gsap';
import { Flip } from 'gsap/all';
import { register } from 'swiper/element/bundle';

import { APP_CONFIG } from '../app-config';

import { routes } from './app.routes';
import { AppConfigLoader } from './config/app-config.loader';
import { AddCameraFlowEffects } from './pages/add-camera-flow/redux/add-camera-flow.effects';
import { ADD_CAMERA_FLOW_STORE_KEY, addCameraFlowReducer } from './pages/add-camera-flow/redux/add-camera-flow.reducer';
import { CameraContactsEffects } from './pages/cameras/contacts/redux/camera-contacts.effects';
import { CAMERA_CONTACTS_STORE_KEY, cameraContactsReducer } from './pages/cameras/contacts/redux/camera-contacts.reducer';
import { CamerasEffects } from './pages/cameras/redux/cameras.effects';
import { camerasReducer } from './pages/cameras/redux/cameras.reducer';
import { RemoteControlEffects } from './pages/cameras/redux/remote-control.effects';
import { REMOTE_CONTROL_STORE_KEY, remoteControlReducer } from './pages/cameras/redux/remote-control.reducer';
import { AlbumEffects } from './pages/gallery/albums/redux/album.effects';
import { albumReducer } from './pages/gallery/albums/redux/album.reducer';
import { FilterEffects } from './pages/gallery/image-gallery/filter/redux/filter.effects';
import { FILTER_STORE_KEY, filterReducer } from './pages/gallery/image-gallery/filter/redux/filter.reducer';
import { GalleryEffects } from './pages/gallery/image-gallery/redux/gallery.effects';
import { GALLERY_STORE_KEY, galleryReducer } from './pages/gallery/image-gallery/redux/gallery.reducer';
import { ImageEffects } from './pages/gallery/image-gallery/redux/image.effects';
import { imageReducer } from './pages/gallery/image-gallery/redux/image.reducer';
import { ConsentEffects } from './pages/menu/consent-modal/redux/consent.effects';
import { CONSENT_STORE_KEY, consentReducer } from './pages/menu/consent-modal/redux/consent.reducer';
import { WhatsNewEffects } from './pages/whats-new/redux/whats-new.effects';
import { WHATS_NEW_STORE_KEY, whatsNewReducer } from './pages/whats-new/redux/whats-new.reducer';
import { MenuEffects } from './pages/menu/redux/menu.effects';
import { MENU_STORE_KEY, menuReducer } from './pages/menu/redux/menu.reducer';
import { provideMenuContacts } from './pages/menu-contacts/redux/menu-contacts.config';
import { apiErrorToastInterceptor } from './shared/interceptors/api-error-toast.interceptor';
import { postCacheInterceptor } from './shared/interceptors/post-cache.interceptor';
import { GeneralEffects } from './shared/redux/general/general.effects';
import { GENERAL_STORE_KEY, generalReducer } from './shared/redux/general/general.reducer';
import { SettingsEffects } from './shared/redux/settings/settings.effects';
import { SETTINGS_STORE_KEY, settingsReducer } from './shared/redux/settings/settings.reducer';
import { TabBarEffects } from './shared/redux/tab-bar/tab-bar.effects';
import { TAB_BAR_STORE_KEY, tabBarReducer } from './shared/redux/tab-bar/tab-bar.reducer';
import { FTP_TRANSPORT } from './shared/services/ftp-transport/ftp-transport.port';
import { FtpTransportStub } from './shared/services/ftp-transport/ftp-transport.stub';
import { AuthStorageService } from './shared/services/auth-storage.service';
import { AuthenticationService } from './shared/services/authentication.service';
import { BleDeviceManagerFacade, REAL_BLE_MANAGER } from './shared/services/BLE/ble-device-manager.facade';
import { BleDeviceManagerService } from './shared/services/BLE/ble-device-manager.service';
import { CacheService } from './shared/services/cache.service';
import { CannesWifiConnectionFacade, REAL_CANNES_WIFI_CONNECTION } from './shared/services/cannes-wifi-connection.facade';
import { CannesWifiConnectionService } from './shared/services/cannes-wifi-connection.service';
import { CannesWifiFacade, REAL_CANNES_WIFI } from './shared/services/cannes-wifi.facade';
import { CannesWifiService } from './shared/services/cannes-wifi.service';
import { GeneralService } from './shared/services/general.service';
import { DeviceRegistrationEffects } from './pages/hunting-products/redux/device-registration.effects';
import { DEVICE_REGISTRATION_STORE_KEY, deviceRegistrationReducer } from './pages/hunting-products/redux/device-registration.reducer';
import { HUNTING_PRODUCTS_STORE_KEY, huntingProductsReducer } from './pages/hunting-products/redux/hunting-products.reducer';
import { HUNTING_PRODUCT_SYNC_STORE_KEY, huntingProductSyncReducer } from './shared/redux/hunting-product-sync/hunting-product-sync.reducer';
import { MockService } from './shared/services/mock-service';
import { MockBleDeviceManagerService } from './mocks/hunting-device/mock-ble-device-manager.service';
import { MockCannesWifiConnectionService } from './mocks/hunting-device/mock-cannes-wifi-connection.service';
import { MockCannesWifiService } from './mocks/hunting-device/mock-cannes-wifi.service';
import { MockHuntingDeviceGate } from './mocks/hunting-device/mock-hunting-device-gate.service';
import { MockHuntingMediaTransport } from './mocks/hunting-device/mock-hunting-media-transport';
import { NetworkService } from './shared/services/network.service';
import { translocoConfig } from './transloco.config';
import { NOTIFICATION_SETTINGS_STORE_KEY, notificationSettingsReducer } from './pages/menu/notification-settings/redux/notification-settings.reducer';
import { NotificationSettingsEffects } from './pages/menu/notification-settings/redux/notification-settings.effects';
import { SettingsService } from './shared/services/settings.service';

// register SwiperJS globally
register();
gsap.registerPlugin(Flip);

let authStorage = null;

export const appConfig = {
  providers: [
    //  importProvidersFrom(),
    provideAppInitializer(async () => {
      authStorage = inject(AuthStorageService);
      const appConfigLoader = inject(AppConfigLoader);
      const mockService = inject(MockService);
      const authService = inject(AuthenticationService);
      const cacheService = inject(CacheService);
      const generalService = inject(GeneralService);
      const networkService = inject(NetworkService);
      // inject() only works synchronaneously in the injection context — grab these up front.
      const settingsService = inject(SettingsService);
      const location = inject(Location);

      await authStorage.init$();
      await appConfigLoader.init();
      await mockService.init$();
      await authService.init$();
      await cacheService.clearExpired();
      await generalService.init$();
      await networkService.init$();

      // Native: on cold start, start the app directly in the most recently used tab.
      // Rewrite the URL to the persisted last tab BEFORE the router performs its
      // initial navigation — no default-route landing, no visible redirect.
      // Covers both a fresh WebView boot ('/', '/home') and a WebView restored at a
      // stale tab root (e.g. '/home/gallery' while the persisted tab is Cameras);
      // deeper URLs (album, camera details) are left untouched.
      if (Capacitor.isNativePlatform()) {
        const pathname = globalThis.location.pathname;
        const isRootOrTabRoot = ['', '/', '/home', '/home/gallery', '/home/cameras'].includes(pathname);
        if (isRootOrTabRoot) {
          const lastTab = await settingsService.getLastTab$(TabType.Gallery);
          const tabRoute = lastTab === TabType.Cameras ? '/home/cameras' : '/home/gallery'; /* Ballistics & Map: extend mapping when restored */
          if (pathname !== tabRoute) {
            location.replaceState(tabRoute);
          }
        }
      }
    }),
    // eslint-disable-next-line @typescript-eslint/no-deprecated,sonarjs/deprecation
    provideAnimations(), // @TODO: Angular v23 migration – animations provider replacement
    provideZonelessChangeDetection(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      mode: 'ios',
      swipeBackEnabled: true, // Enable/Disable iOS swipe-back gesture globally
    }),
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      // withDebugTracing(),
      withComponentInputBinding()
    ), // TODO: disabled preloading for web version
    provideHttpClient(
      withInterceptors([authInterceptor(), apiErrorToastInterceptor, postCacheInterceptor])
      //  withFetch()
    ),
    // i18n
    provideTransloco({
      config: {
        ...translocoConfig,
        defaultLang: computeDefaultLanguage(translocoConfig),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat({
      locales: ['en-US', 'de-DE'],
    }),
    // Redux
    provideEffects([
      GeneralEffects,
      AddCameraFlowEffects,
      CamerasEffects,
      CameraContactsEffects,
      RemoteControlEffects,
      NotificationSettingsEffects,
      GalleryEffects,
      AlbumEffects,

      FilterEffects,

      ImageEffects,
      SettingsEffects,
      MenuEffects,
      ConsentEffects,
      WhatsNewEffects,
      TabBarEffects,
      DeviceRegistrationEffects,
    ]),
    provideStore({
      [GENERAL_STORE_KEY]: generalReducer,
      [GALLERY_STORE_KEY]: galleryReducer,
      [ADD_CAMERA_FLOW_STORE_KEY]: addCameraFlowReducer,
      cameras: camerasReducer,
      [CAMERA_CONTACTS_STORE_KEY]: cameraContactsReducer,
      [REMOTE_CONTROL_STORE_KEY]: remoteControlReducer,
      [NOTIFICATION_SETTINGS_STORE_KEY]: notificationSettingsReducer,
      album: albumReducer,
      [MENU_STORE_KEY]: menuReducer,
      [CONSENT_STORE_KEY]: consentReducer,
      [WHATS_NEW_STORE_KEY]: whatsNewReducer,

      // filter: filterReducer,
      [FILTER_STORE_KEY]: filterReducer,

      image: imageReducer,
      [SETTINGS_STORE_KEY]: settingsReducer,
      [TAB_BAR_STORE_KEY]: tabBarReducer,
      [DEVICE_REGISTRATION_STORE_KEY]: deviceRegistrationReducer,
      [HUNTING_PRODUCTS_STORE_KEY]: huntingProductsReducer,
      [HUNTING_PRODUCT_SYNC_STORE_KEY]: huntingProductSyncReducer,
    }),
    provideStoreDevtools({
      maxAge: 250,
      serialize: true,
      logOnly: true,
      connectInZone: true,
      trace: false,
      actionsBlocklist: ['[Gallery] Side load images'],
    }),
    // FTP transport — stub until the real Capacitor plugin lands (see story 1650895 findings.md)
    { provide: FTP_TRANSPORT, useClass: FtpTransportStub },
    // Dev-only hunting-product device mock — swaps 4 DI seams so the real settings
    // screen + sync engine run against bundled sample media with no device/firmware attached.
    // FEATURE_MOCK_HUNTING_DEVICE folds to a literal at build time (see project.json's `define`
    // blocks), so esbuild dead-code-eliminates this whole branch — and the mock classes it imports
    // — from every prod/*-web bundle.
    ...(FEATURE_MOCK_HUNTING_DEVICE
      ? [
          // BLE is runtime-toggled by MockHuntingDeviceGate (via BleDeviceManagerFacade), not swapped
          // here — so real scanning/pairing keeps working until the dev toggle is switched on.
          { provide: REAL_BLE_MANAGER, useClass: BleDeviceManagerService },
          MockHuntingDeviceGate,
          MockBleDeviceManagerService,
          { provide: BleDeviceManagerService, useClass: BleDeviceManagerFacade },
          { provide: FTP_TRANSPORT, useClass: MockHuntingMediaTransport },
          { provide: REAL_CANNES_WIFI_CONNECTION, useClass: CannesWifiConnectionService },
          { provide: REAL_CANNES_WIFI, useClass: CannesWifiService },
          MockCannesWifiConnectionService,
          MockCannesWifiService,
          { provide: CannesWifiConnectionService, useClass: CannesWifiConnectionFacade },
          { provide: CannesWifiService, useClass: CannesWifiFacade },
        ]
      : []),
    ...provideMenuContacts(),
    // authentication
    provideAuth(APP_CONFIG.authorization),
    { provide: AbstractSecurityStorage, useExisting: AuthStorageService },
  ],
};
